/**
 * GDPR Compliance Manager
 * Handles privacy consent, data access requests, and user rights under GDPR
 */

export default class GDPRManager {

    static instance = null

    constructor() {
        if (GDPRManager.instance) {
            return GDPRManager.instance
        }

        this.consentKey = 'gdpr_consent'
        this.preferencesKey = 'privacy_preferences'
        this.consentVersion = '1.0'
        this.requiredAge = 13
        
        // Default privacy preferences
        this.defaultPreferences = {
            analytics: false,
            marketing: false,
            personalization: false,
            essential: true, // Always true, cannot be disabled
            crashReporting: false
        }

        this.currentPreferences = this.loadPreferences()
        this.consentGiven = this.hasValidConsent()

        GDPRManager.instance = this
    }

    static getInstance() {
        if (!GDPRManager.instance) {
            new GDPRManager()
        }
        return GDPRManager.instance
    }

    /**
     * Check if user has given valid consent
     */
    hasValidConsent() {
        try {
            const consent = localStorage.getItem(this.consentKey)
            if (!consent) return false

            const consentData = JSON.parse(consent)
            return consentData.version === this.consentVersion && 
                   consentData.timestamp && 
                   (Date.now() - consentData.timestamp) < (365 * 24 * 60 * 60 * 1000) // 1 year
        } catch (error) {
            console.error('[GDPR] Error checking consent:', error)
            return false
        }
    }

    /**
     * Record user consent
     * COPPA/GDPR-K Compliance (Finding S2): Uses minimal platform category instead of full userAgent
     */
    recordConsent(preferences = {}) {
        const consentData = {
            version: this.consentVersion,
            timestamp: Date.now(),
            preferences: { ...this.defaultPreferences, ...preferences },
            // Finding S2: Minimize userAgent - only store platform category
            platform: this._getPlatformCategory(),
            consentMethod: 'explicit_click',
            ip: null // Will be recorded server-side
        }

        try {
            localStorage.setItem(this.consentKey, JSON.stringify(consentData))
            localStorage.setItem(this.preferencesKey, JSON.stringify(consentData.preferences))
            
            this.currentPreferences = consentData.preferences
            this.consentGiven = true

            // Notify other systems about consent change
            this.notifyConsentChange()

            return true
        } catch (error) {
            console.error('[GDPR] Error recording consent:', error)
            return false
        }
    }

    /**
     * COPPA/GDPR-K Compliance (Finding S2): Get minimal platform category
     * Returns only device type category instead of full userAgent string
     */
    _getPlatformCategory() {
        const ua = navigator.userAgent.toLowerCase()
        if (/mobile|android|iphone|ipad|ipod/.test(ua)) return 'mobile'
        if (/windows/.test(ua)) return 'windows'
        if (/mac/.test(ua)) return 'macos'
        if (/linux/.test(ua)) return 'linux'
        if (/cros/.test(ua)) return 'chromeos'
        return 'other'
    }

    /**
     * Withdraw consent (sets all non-essential to false)
     */
    withdrawConsent() {
        const withdrawnPreferences = {
            ...this.defaultPreferences,
            analytics: false,
            marketing: false,
            personalization: false,
            crashReporting: false
        }

        return this.recordConsent(withdrawnPreferences)
    }

    /**
     * Load current privacy preferences
     */
    loadPreferences() {
        try {
            const preferences = localStorage.getItem(this.preferencesKey)
            return preferences ? JSON.parse(preferences) : this.defaultPreferences
        } catch (error) {
            console.error('[GDPR] Error loading preferences:', error)
            return this.defaultPreferences
        }
    }

    /**
     * Update specific privacy preference
     */
    updatePreference(category, value) {
        if (category === 'essential') {
            console.warn('[GDPR] Essential cookies cannot be disabled')
            return false
        }

        this.currentPreferences[category] = value
        return this.recordConsent(this.currentPreferences)
    }

    /**
     * Get current preference for a category
     */
    getPreference(category) {
        return this.currentPreferences[category] || false
    }

    /**
     * Check if analytics collection is allowed
     */
    canCollectAnalytics() {
        return this.consentGiven && this.getPreference('analytics')
    }

    /**
     * Check if marketing data collection is allowed
     */
    canCollectMarketing() {
        return this.consentGiven && this.getPreference('marketing')
    }

    /**
     * Check if personalization is allowed
     */
    canPersonalize() {
        return this.consentGiven && this.getPreference('personalization')
    }

    /**
     * Check if crash reporting is allowed
     */
    canCollectCrashReports() {
        return this.consentGiven && this.getPreference('crashReporting')
    }

    /**
     * Request data download (GDPR Article 15 - Right of Access)
     */
    async requestDataDownload() {
        if (!window.game?.network?.connected) {
            throw new Error('Must be connected to request data download')
        }

        try {
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Data download request timed out'))
                }, 30000)

                window.game.network.socket.once('gdpr_data_export', (response) => {
                    clearTimeout(timeout)
                    if (response.success) {
                        resolve(response.downloadUrl)
                    } else {
                        reject(new Error(response.error || 'Failed to generate data export'))
                    }
                })

                window.game.network.send('request_data_export', {
                    requestType: 'full_export',
                    format: 'json'
                })
            })
        } catch (error) {
            console.error('[GDPR] Error requesting data download:', error)
            throw error
        }
    }

    /**
     * Request account deletion (GDPR Article 17 - Right to Erasure)
     */
    async requestAccountDeletion(reason = '') {
        if (!window.game?.network?.connected) {
            throw new Error('Must be connected to request account deletion')
        }

        try {
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Account deletion request timed out'))
                }, 30000)

                window.game.network.socket.once('gdpr_account_deletion', (response) => {
                    clearTimeout(timeout)
                    if (response.success) {
                        // Clear all local data
                        this.clearAllLocalData()
                        resolve(response)
                    } else {
                        reject(new Error(response.error || 'Failed to process deletion request'))
                    }
                })

                window.game.network.send('request_account_deletion', {
                    reason: reason,
                    confirmDeletion: true,
                    timestamp: Date.now()
                })
            })
        } catch (error) {
            console.error('[GDPR] Error requesting account deletion:', error)
            throw error
        }
    }

    /**
     * Clear all local data (for account deletion or privacy)
     */
    clearAllLocalData() {
        try {
            // Clear GDPR related data
            localStorage.removeItem(this.consentKey)
            localStorage.removeItem(this.preferencesKey)
            
            // Clear all game-related localStorage
            const keysToRemove = []
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key && (key.startsWith('game_') || key.startsWith('user_') || 
                           key.startsWith('settings_') || key.startsWith('cache_'))) {
                    keysToRemove.push(key)
                }
            }
            
            keysToRemove.forEach(key => localStorage.removeItem(key))
            
            // Clear sessionStorage
            sessionStorage.clear()
            
            // Clear any cookies (if any are set)
            this.clearCookies()
            
            console.log('[GDPR] All local data cleared')
        } catch (error) {
            console.error('[GDPR] Error clearing local data:', error)
        }
    }

    /**
     * Clear cookies
     */
    clearCookies() {
        const cookies = document.cookie.split(";")
        for (let cookie of cookies) {
            const eqPos = cookie.indexOf("=")
            const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`
        }
    }

    /**
     * Get age verification status
     */
    getAgeVerificationStatus() {
        const ageConsent = localStorage.getItem('age_verification')
        if (!ageConsent) return { verified: false, age: null }
        
        try {
            return JSON.parse(ageConsent)
        } catch {
            return { verified: false, age: null }
        }
    }

    /**
     * Verify user age for GDPR compliance
     */
    verifyAge(birthYear) {
        const currentYear = new Date().getFullYear()
        const age = currentYear - birthYear
        const isOfAge = age >= this.requiredAge
        
        const ageData = {
            verified: true,
            age: age,
            isOfAge: isOfAge,
            timestamp: Date.now()
        }
        
        try {
            localStorage.setItem('age_verification', JSON.stringify(ageData))
            return ageData
        } catch (error) {
            console.error('[GDPR] Error storing age verification:', error)
            return ageData
        }
    }

    /**
     * Notify other systems about consent changes
     */
    notifyConsentChange() {
        // Dispatch custom event for other systems to listen to
        window.dispatchEvent(new CustomEvent('gdprConsentChanged', {
            detail: {
                preferences: this.currentPreferences,
                consentGiven: this.consentGiven
            }
        }))
    }

    /**
     * Get data processing purposes and legal basis
     */
    getDataProcessingInfo() {
        return {
            purposes: [
                {
                    category: 'essential',
                    purpose: 'Core game functionality',
                    legalBasis: 'Legitimate interest',
                    description: 'Required for basic game operation, user authentication, and security',
                    canOptOut: false
                },
                {
                    category: 'analytics',
                    purpose: 'Game improvement and analytics',
                    legalBasis: 'Consent',
                    description: 'Anonymous usage statistics to improve game experience',
                    canOptOut: true
                },
                {
                    category: 'personalization',
                    purpose: 'Personalized experience',
                    legalBasis: 'Consent',
                    description: 'Customize game content and recommendations based on your preferences',
                    canOptOut: true
                },
                {
                    category: 'crashReporting',
                    purpose: 'Error tracking and debugging',
                    legalBasis: 'Consent',
                    description: 'Automatic error reporting to help fix bugs and improve stability',
                    canOptOut: true
                },
                {
                    category: 'marketing',
                    purpose: 'Marketing communications',
                    legalBasis: 'Consent',
                    description: 'Promotional emails and in-game announcements',
                    canOptOut: true
                }
            ],
            dataTypes: [
                'Username and email address',
                'Game progress and statistics',
                'In-game purchases and virtual items',
                'Chat messages (stored temporarily)',
                'Technical data (IP address, browser info)',
                'Usage analytics (anonymous)'
            ],
            retentionPeriods: {
                gameData: '3 years after last activity',
                analytics: '2 years',
                crashReports: '1 year',
                chatLogs: '30 days',
                accountData: 'Until account deletion'
            }
        }
    }
}